import {
  AuthRepositoryInterface,
  LoginParams,
  RegisterDeviceIdParams,
  SignUpParams,
} from '../domain/AuthRepositoryInterface';
import AuthService from 'src/features/auth/data/service/authService';
import { UserDetails, ErrorResponsePayload, Session } from 'src/types/common';
import { inject, injectable } from 'inversify';
import { removeStorageSessionData } from 'src/utils/localStorage/session';
import growthbook from 'src/utils/growthBook/growthBook';

@injectable()
class AuthRepository implements AuthRepositoryInterface {
  @inject(AuthService) private authService!: AuthService;

  async login(params: LoginParams) {
    try {
      const response = await this.authService.login(params);

      const { access_token, user } = response.data;

      if (!access_token) throw new Error('Access token missing in response.');

      this.setSessionData(access_token, user);

      return { accessToken: access_token, user };
    } catch (error) {
      throw new Error(this.parseError(error));
    }
  }

  async signUp(params: SignUpParams) {
    try {
      const response = await this.authService.register(params);

      const { access_token, user } = response.data;

      if (!access_token) throw new Error('Access token missing in response.');

      this.setSessionData(access_token, user);

      return { accessToken: access_token, user };
    } catch (error) {
      throw new Error(this.parseError(error));
    }
  }

  async logOut() {
    try {
      const sessionStorageValue = localStorage.getItem('session');
      const session = sessionStorageValue
        ? (JSON.parse(sessionStorageValue) as Session)
        : null;
      const token = session?.accessToken;
      await growthbook.updateAttributes({ id: null });
      if (!token) {
        this.removeSessionData();
        throw new Error('Access token not found');
      }

      const response = await this.authService.logout({ token });

      this.removeSessionData();

      return response.data;
    } catch (error) {
      this.removeSessionData();
      throw new Error(this.parseError(error));
    }
  }

  async resetPassword(params: { email: string }) {
    const response = await this.authService.resetPassword(params);

    return response.data;
  }

  async registerDeviceId({ device_id }: RegisterDeviceIdParams) {
    try {
      const sessionStorageValue = localStorage.getItem('session');
      const session = sessionStorageValue
        ? (JSON.parse(sessionStorageValue) as Session)
        : null;
      const token = session?.accessToken;
      const user_id = session?.user.pk;

      if (!token || !user_id) {
        this.removeSessionData();
        throw new Error('Access token not found');
      }

      const response = await this.authService.registerDeviceId({
        device_id: device_id,
        user_id,
        token,
      });

      return response.data;
    } catch (error) {
      throw new Error(this.parseError(error));
    }
  }

  async registerJwt(params: { token: string }) {
    try {
      const response = await this.authService.registerJwt(params);
      return response.data;
    } catch (error) {
      throw new Error(this.parseError(error));
    }
  }

  private setSessionData(accessToken: string, user: UserDetails) {
    localStorage.setItem('session', JSON.stringify({ accessToken, user }));
  }

  private removeSessionData() {
    removeStorageSessionData();
  }

  private parseError(error: unknown) {
    return JSON.stringify(
      (error as Error).message
        ? { message: [(error as Error).message] }
        : (error as ErrorResponsePayload)
    );
  }

  async getSocialProfile(params: {
    provider: 'google' | 'facebook' | 'apple';
  }) {
    const provider = params.provider;

    return await this.authService.getSocialProfile({ provider });
  }

  async getDeviceIpAddress() {
    const response = await this.authService.getDeviceIpAddress();

    return response.data.ip;
  }
}

export default AuthRepository;
